/*
  File: src/components/Stream/StreamingControls.jsx
  Purpose: User controls panel for starting/stopping a live stream and showing status.
  Responsibilities:
  - Display current streaming state and any error from StreamingContext.
  - Provide Start and Stop buttons with loading states.
  - Offer a dismissible error message area.
*/
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useStreaming, STREAMING_STATES } from '../../contexts/StreamingContext';

const StreamingControls = ({ onStop = null }) => {
    const navigate = useNavigate();
    const {
        streamingState,
        isLoading,
        error,
        startStream,
        stopStream,
        clearError,
        streamType,
        streamSessionId
    } = useStreaming();

    const handleStartStream = async () => {
        await startStream(streamType || 'match');
    };

    const handleStopStream = async () => {
        if (onStop) {
            await onStop();
        } else {
            await stopStream();
        }
    };

    const getStatusIcon = () => {
        switch (streamingState) {
            case STREAMING_STATES.LIVE:
                return '🔴';
            case STREAMING_STATES.STARTING:
                return '⏳';
            case STREAMING_STATES.STOPPING:
                return '⏹️';
            case STREAMING_STATES.ERROR:
                return '❌';
            default:
                return '⚫';
        }
    };

    const getStatusText = () => {
        switch (streamingState) {
            case STREAMING_STATES.LIVE:
                return 'Live Stream';
            case STREAMING_STATES.STARTING:
                return 'Starting...';
            case STREAMING_STATES.STOPPING:
                return 'Stopping...';
            case STREAMING_STATES.ERROR:
                return 'Error';
            default:
                return 'Offline';
        }
    };

    const getStatusStyle = () => {
        switch (streamingState) {
            case STREAMING_STATES.LIVE:
                return 'text-green-400 bg-green-900/20 border-green-500/30';
            case STREAMING_STATES.STARTING:
                return 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
            case STREAMING_STATES.ERROR:
                return 'text-red-400 bg-red-900/20 border-red-500/30';
            default:
                return 'text-gray-400 bg-gray-900/20 border-gray-500/30';
        }
    };

    return (
        <div className="card p-4 space-y-4">
            {/* Error Display */}
            {error && (
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-red-400">
                            <span>❌</span>
                            <span className="text-sm">{error}</span>
                        </div>
                        <button
                            onClick={clearError}
                            className="text-red-400 hover:text-red-300 text-xs"
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}

            {/* Stream Status */}
            <div className="flex items-center justify-between">
                <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${getStatusStyle()}`}>
                    <span className="text-xl">{getStatusIcon()}</span>
                    <div>
                        <div className="text-sm font-semibold">
                            {getStatusText()}
                        </div>
                        {streamingState === STREAMING_STATES.LIVE && (
                            <div className="text-xs opacity-75">
                                Stream is broadcasting
                            </div>
                        )}
                        {streamingState === STREAMING_STATES.IDLE && (
                            <div className="text-xs opacity-75">
                                Ready to start streaming
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Control Buttons - Watch page only shows stop functionality */}
            <div className="flex gap-2">
                {streamingState === STREAMING_STATES.LIVE || streamingState === STREAMING_STATES.STOPPING ? (
                    <button
                        onClick={handleStopStream}
                        disabled={isLoading || streamingState === STREAMING_STATES.STOPPING}
                        className="flex-1 btn-ghost text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading || streamingState === STREAMING_STATES.STOPPING ? 'Stopping…' : 'Stop Stream'}
                    </button>
                ) : (
                    <div className="flex-1 text-center text-white/60 px-4 py-2 text-sm">
                        {`Stream controls available from ${streamType === 'series' ? 'Series' : 'Football'} page`}
                    </div>
                )}
            </div>

            {/* Removed: Add Current Stream to Dataset (available in /history only) */}

            {/* History Button - only show when REACT_APP_SHOW_DEV_PAGES is true */}
            {process.env.REACT_APP_SHOW_DEV_PAGES === 'true' && (
                <div>
                    <button
                        onClick={() => navigate('/history')}
                        className="w-full btn-ghost text-white px-4 py-2 rounded text-sm font-medium"
                    >
                        View History
                    </button>
                </div>
            )}
        </div>
    );
};

export default StreamingControls;
